/**
 * Send for review — one entry point for both lanes.
 *
 * The original product had two buttons, "Notify" and "Share All", side by side
 * on the collection page. Choosing between them required knowing that one
 * raises an assigned task and the other mints a signed public link. The only
 * question a user actually has is *who is reviewing this*, so that is the only
 * question this asks; the audience picks the mechanism.
 *
 * Steps, skipped when already answered:
 *
 *   collection  only when opened from somewhere with no assets in hand — i.e.
 *               the Reviews page. From a collection this never appears.
 *   audience    the one real question.
 *   internal    the form, rendered here (ReviewInternalStep).
 *   external    hands off to AddAssetsToShare with the review-approve preset
 *               locked in, because that modal already knows how to build a
 *               share and duplicating it to save one handoff would be worse.
 *
 * The mechanism is still named under each audience card, in small mono type.
 * Hiding the choice is the point; hiding the *fact* would leave anyone who
 * does understand shares unable to predict what they are about to send.
 */
import React, { useMemo, useState } from 'react';

import { BaseModal, Button, Input, Shimmer, AddAssetsToShare } from 'src/components';
import { Checks, Users, ShareNetwork } from 'src/common/icons';
import useModal from 'src/hooks/useModal';
import { useAllCollectionsQuery } from 'src/api/queries/collectionsQueries';
import { CollectionSearchQuery } from 'src/api/functions/collectionsApi';

import ReviewInternalStep from './ReviewInternalStep';
import styles from './styles.module.scss';

type Step = 'collection' | 'audience' | 'internal';

type PickedCollection = {
  id: number;
  name: string;
  groupAccess: boolean;
  assetIds: string[];
};

export type SendForReviewModalProps = {
  /** Present when opened from a collection; absent from the Reviews page,
   *  which then starts on the picker. */
  collection?: PickedCollection;
  /** Seeds the internal branch's filters from the page's current view. */
  initialQuery?: CollectionSearchQuery;
};

export const SendForReviewModal: React.FC<SendForReviewModalProps> = ({
  collection,
  initialQuery,
}) => {
  const { showModal, closeModal } = useModal();

  const [picked, setPicked] = useState<PickedCollection | undefined>(collection);
  const [step, setStep] = useState<Step>(collection ? 'audience' : 'collection');
  const [search, setSearch] = useState('');

  // Only fetched when the picker is actually shown.
  const { data: collectionsData, isLoading: loadingCollections } = useAllCollectionsQuery(
    undefined,
    { enabled: step === 'collection' },
  );

  const collections = useMemo(() => {
    const all: any[] = collectionsData?.data?.collections ?? [];
    const withAssets = all.filter((c) => (c.assets?.length ?? 0) > 0);
    const term = search.trim().toLowerCase();
    if (!term) return withAssets;
    return withAssets.filter((c) => (c.name || '').toLowerCase().includes(term));
  }, [collectionsData, search]);

  const choose = (c: any) => {
    setPicked({
      id: c.id,
      name: c.name || 'Collection',
      groupAccess: Boolean(c.group_access),
      assetIds: c.assets ?? [],
    });
    setStep('audience');
  };

  const goExternal = () => {
    if (!picked) return;
    // Replace this modal rather than stacking one on another: the wizard's
    // job is done once the audience is known, and AddAssetsToShare owns the
    // whole external form.
    closeModal();
    showModal(
      <AddAssetsToShare assetIds={picked.assetIds} lockedPresetId="review-approve" />,
    );
  };

  // The collection step disappears entirely when we already have one, so the
  // numbering has to come from the steps actually being shown — otherwise the
  // collection-page flow opens on "Step 2 of 3".
  const steps: Step[] = collection
    ? ['audience', 'internal']
    : ['collection', 'audience', 'internal'];
  const stepNumber = steps.indexOf(step) + 1;

  return (
    <BaseModal>
      <div className={styles.wizHead}>
        <span className={styles.wizSteps}>
          Step {stepNumber} of {steps.length}
        </span>
        <h3 className={styles.wizTitle}>
          {step === 'collection' && 'Which assets?'}
          {step === 'audience' && 'Who’s reviewing?'}
          {step === 'internal' && 'Send for review'}
        </h3>
        {picked && step !== 'collection' && (
          <p className={styles.wizSub}>
            {picked.assetIds.length} asset{picked.assetIds.length === 1 ? '' : 's'} in{' '}
            <strong>{picked.name}</strong>
          </p>
        )}
      </div>

      {step === 'collection' && (
        <>
          <div className={styles.wizStep}>
            <Input
              placeholder="Search collections..."
              value={search}
              onChange={setSearch}
              variant="secondary"
            />

            {loadingCollections && (
              <div className={styles.pickerList}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Shimmer key={i} w="100%" h="3rem" radius="0.5rem" />
                ))}
              </div>
            )}

            {!loadingCollections && collections.length === 0 && (
              <p className={styles.wizNote}>
                {search
                  ? 'No collections match that search.'
                  : 'No collections with assets yet. Add assets to a collection, then send it for review.'}
              </p>
            )}

            {!loadingCollections && collections.length > 0 && (
              <ul className={styles.pickerList}>
                {collections.map((c) => (
                  <li key={c.id}>
                    <button type="button" className={styles.pickerItem} onClick={() => choose(c)}>
                      <span className={styles.pickerName}>{c.name || 'Untitled'}</span>
                      <span className={styles.pickerMeta}>
                        {c.assets?.length ?? 0} asset{(c.assets?.length ?? 0) === 1 ? '' : 's'}
                        {c.group_access ? ' · shared with group' : ' · private'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.wizActions}>
            <Button size="small" mode="tertiary" onClick={closeModal}>
              Cancel
            </Button>
          </div>
        </>
      )}

      {step === 'audience' && (
        <>
          <div className={styles.wizStep}>
            <div className={styles.choice}>
              <button type="button" onClick={() => setStep('internal')}>
                <span className={`${styles.choiceIcon} ${styles.choiceIconInternal}`}>
                  <Users />
                </span>
                <span>
                  <span className={styles.choiceTitle}>People in my team</span>
                  <span className={styles.choiceDesc}>
                    They get a task in FileSpin plus an email, and mark it done, declined or
                    dismissed.
                  </span>
                  <span className={styles.choiceMech}>
                    internal · assigned task · reply-to you
                  </span>
                </span>
              </button>

              <button type="button" onClick={goExternal}>
                <span className={`${styles.choiceIcon} ${styles.choiceIconExternal}`}>
                  <ShareNetwork />
                </span>
                <span>
                  <span className={styles.choiceTitle}>People outside the account</span>
                  <span className={styles.choiceDesc}>
                    They get a private link — no login. They approve or reject each asset and
                    leave comments.
                  </span>
                  <span className={styles.choiceMech}>
                    external · review link · watermarked previews
                  </span>
                </span>
              </button>
            </div>

            <p className={styles.wizNote}>
              <Checks className={styles.wizNoteIcon} />
              Either way it appears under Reviews until it comes back.
            </p>
          </div>

          <div className={styles.wizActions}>
            {!collection && (
              <Button size="small" mode="tertiary" onClick={() => setStep('collection')}>
                Back
              </Button>
            )}
            <Button size="small" mode="tertiary" onClick={closeModal}>
              Cancel
            </Button>
          </div>
        </>
      )}

      {step === 'internal' && picked && (
        <ReviewInternalStep
          collectionId={picked.id}
          collectionName={picked.name}
          groupAccess={picked.groupAccess}
          initialQuery={initialQuery}
          onBack={() => setStep('audience')}
        />
      )}
    </BaseModal>
  );
};

export default SendForReviewModal;
